/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useEffect, useState, Fragment } from 'react';
import { useFormikContext, getIn } from 'formik';
import { navigateToError } from '@utils';
import { Alert } from '@components';
import CourtStep4AlertContent from '@components/Content/AlertContent/CourtStep4AlertContent';
import { iFineDescriptions, iPartialFormData } from '@interfaces/interfaces';
import { ObligationStatus } from '@constants';
import { iCourtFormValues } from '@services/CourtService';
import classnames from 'classnames';
import InfoIcon from '../../../../images/info.svg';
import styles from './CourtStep4.module.scss';

const { REACT_APP_SITECORE_API_ENDPOINT: API_URL } = process.env;

function CourtStep4(): ReactElement {
  const [loading, setLoading] = useState(false);
  const [seriousObligation, setSeriousObligation] = useState(false);
  const context = useFormikContext();
  const { values }: { values: iCourtFormValues } = useFormikContext();
  const expiryDate = `${values.PartialFormExpiryDate.substring(0, values.PartialFormExpiryDate.indexOf('day') + 3)}, ${values.PartialFormExpiryDate.substring(
    values.PartialFormExpiryDate.indexOf('day') + 4,
  )}`;
  const titles = [
    { value: ObligationStatus.INFRINGEMENT, display: 'Infringement Notice' },
    { value: ObligationStatus.PENALTY_REMINDER, display: 'Penalty Reminder Notice' },
    { value: ObligationStatus.NOTICE_OF_FINAL_DEMAND, display: 'Notice of Final Demand' },
    { value: ObligationStatus.ENFORCEMENT_WARRANT, display: 'Enforcement Warrant' },
  ];

  const [obligation, setObligationDetails] = useState({ Obligation: { Number: '' } } as iFineDescriptions);
  const { Obligation } = obligation;
  function setValues(partialFormApi: any, formikObject: any, keyName: string) {
    Object.keys(formikObject).forEach((key) => {
      if (key in partialFormApi) {
        if (typeof formikObject[key] === 'object') {
          setValues(partialFormApi[key], formikObject[key], `${keyName}.${key}`);
        } else {
          context.setFieldValue(`${keyName}.${key}`, getIn(partialFormApi, key));
        }
      }
    });
  }

  useEffect(() => {
    const obligationPayload = {
      obligationNumber: values.OBLIGATION_NUMBER,
    };
    const formPayload = {
      partialFormID: values.PartialFormID,
    };

    const getExistingForm = async (): Promise<void> => {
      setLoading(true);
      const url = `${API_URL}sitecore/PartialForms/GetPartialForm`;
      let response: any;
      try {
        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formPayload),
        });
        const responseBody = await response.json();
        if (response.status === 200) {
          if (typeof responseBody === 'string') {
            if (responseBody === 'Unauthorised') {
              setLoading(false);
              navigateToError();
            } else {
              return;
            }
          } else {
            responseBody.FormData = JSON.parse(responseBody.FormData);
            const keys = Object.keys(responseBody);
            keys.forEach((key) => {
              if (key === 'FormData') {
                const formDataKeys = Object.keys(responseBody.FormData);
                setValues(responseBody.FormData, values.FormData, 'FormData');
                if (formDataKeys.includes('user')) {
                  context.setFieldValue('SummonsAddress.LineOne', responseBody.FormData.user.postalAddress.line1 ? responseBody.FormData.user.postalAddress.line1 : '');
                  context.setFieldValue('SummonsAddress.LineTwo', responseBody.FormData.user.postalAddress.line2 ? responseBody.FormData.user.postalAddress.line2 : '');
                  context.setFieldValue('SummonsAddress.Suburb', responseBody.FormData.user.postalAddress.suburb ? responseBody.FormData.user.postalAddress.suburb : '');
                  context.setFieldValue('SummonsAddress.State', responseBody.FormData.user.postalAddress.state ? responseBody.FormData.user.postalAddress.state : '');
                  context.setFieldValue('SummonsAddress.Postcode', responseBody.FormData.user.postalAddress.postcode ? responseBody.FormData.user.postalAddress.postcode : '');
                  if (responseBody.FormData.user.postalAddress.country === '') context.setFieldValue('SummonsAddress.Country', 'Australia');
                  else context.setFieldValue('SummonsAddress.Country', responseBody.FormData.user.postalAddress.country);
                }
                if (formDataKeys.includes('company')) {
                  context.setFieldValue('CompanyAddress.LineOne', responseBody.FormData.company.postalAddress.line1 ? responseBody.FormData.company.postalAddress.line1 : '');
                  context.setFieldValue('CompanyAddress.LineTwo', responseBody.FormData.company.postalAddress.line2 ? responseBody.FormData.company.postalAddress.line2 : '');
                  context.setFieldValue('CompanyAddress.Suburb', responseBody.FormData.company.postalAddress.suburb ? responseBody.FormData.company.postalAddress.suburb : '');
                  context.setFieldValue('CompanyAddress.State', responseBody.FormData.company.postalAddress.state ? responseBody.FormData.company.postalAddress.state : '');
                  context.setFieldValue('CompanyAddress.Postcode', responseBody.FormData.company.postalAddress.postcode ? responseBody.FormData.company.postalAddress.postcode : '');
                  if (responseBody.FormData.company.postalAddress.country === '') context.setFieldValue('CompanyAddress.Country', 'Australia');
                  else context.setFieldValue('CompanyAddress.Country', responseBody.FormData.company.postalAddress.country);
                }
              } else {
                context.setFieldValue(`${key}`, responseBody[key]);
              }
            });
            setLoading(false);
          }
        }
      } catch (error) {
        console.log(error);
      }
    };
    const getObligationOptions = async (): Promise<void> => {
      setLoading(true);
      const url = `${API_URL}sitecore/Court/GetObligationOptions`;
      let response: any;
      try {
        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(obligationPayload),
        });
        const responseBody = await response.json();
        setLoading(false);
        if (response.status === 200) {
          const keys = Object.keys(responseBody);
          keys.forEach((key) => {
            if (key === 'Obligation') {
              const obligationKeys = Object.keys(responseBody.Obligation);
              obligationKeys.forEach((obligationKey) => {
                setObligationDetails({
                  ...obligation,
                  Obligation: {
                    ...obligation.Obligation,
                    [obligationKey]: responseBody.Obligation[obligationKey],
                  },
                });
                if (obligationKey === 'Number') {
                  context.setFieldValue('OBLIGATION_NUMBER', responseBody.Obligation[obligationKey]);
                }
                if (obligationKey === 'InfringementNumber') {
                  context.setFieldValue('InfringementNoticeNumber', responseBody.Obligation[obligationKey]);
                }
                obligation.Obligation[obligationKey] = responseBody.Obligation[obligationKey];
                if (obligationKey === 'DebtorType') {
                  context.setFieldValue(obligationKey, responseBody.Obligation[obligationKey]);
                }
              });
            } else {
              setObligationDetails({
                ...obligation,
                [key]: responseBody[key],
              });
              obligation[key] = responseBody[key];
            }
          });
        } else navigateToError();
      } catch (error) {
        console.log(error);
      }
    };
    const getBasicInfo = async (): Promise<void> => {
      setLoading(true);
      const url = `${API_URL}sitecore/Court/GetBasicInfo`;
      let response: any;
      try {
        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(obligationPayload),
        });
        const responseBody = await response.json();
        setLoading(false);
        if (response.status === 200) {
          setSeriousObligation(responseBody.SeriousObligation);
          context.setFieldValue('SeriousObligation', responseBody.SeriousObligation);
          context.setFieldValue('DDD', responseBody.DDD);
          context.setFieldValue('ESI', responseBody.ESI);
          context.setFieldValue('MarineOffence', responseBody.MarineOffence);
        } else navigateToError();
      } catch (error) {
        navigateToError();
      }
    };
    if (values.PartialFormID) getExistingForm();
    getObligationOptions();
    getBasicInfo();
  }, []);

  return (
    <>
      {!loading && (
        <>
          {values.CheckingExisting === 'yes' && <Alert icon={InfoIcon} color="red" message={<CourtStep4AlertContent formExpirationDate={expiryDate} />} theme="success" />}
          <div className="vfH2">Your fines</div>
          {seriousObligation ? (
            <p className="subHeadingH3">You're applying to go to court as a Notice of Objection for the following fine:</p>
          ) : (
            <p className="subHeadingH3">You're applying to go to court for the following fine:</p>
          )}
          <div className={styles.offenceDetails}>
            <div className={styles.detailHeadings}>
              <span>OBLIGATION NUMBER</span>
              <span>OFFENCE DATE</span>
              <span>AMOUNT</span>
            </div>
            <div className={styles.detailValues}>
              <span className={styles.obligationNumber}>{obligation.Obligation.Number}</span>
              <span>{obligation.Obligation.OffenceDate}</span>
              <span>{obligation.Obligation.AmountDue}</span>
            </div>
            <div className={styles.detailTable}>
              <table className={styles.offenceTable}>
                <tbody>
                  <tr className={styles.onlyMobileRows}>
                    <td>Offence date</td>
                    <td>{Obligation.OffenceDate}</td>
                  </tr>
                  <tr className={styles.onlyMobileRows}>
                    <td>Amount</td>
                    <td>{Obligation.AmountDue}</td>
                  </tr>
                  <tr>
                    <td>Offence description</td>
                    <td>{Obligation.OffenceDesc}</td>
                  </tr>
                  <tr>
                    <td>Offence location</td>
                    <td className={styles.uppcaseText}>{Obligation.OffenceLocation}</td>
                  </tr>
                  <tr>
                    <td>Time of offence</td>
                    <td>{Obligation.OffenceTime}</td>
                  </tr>
                  <tr>
                    <td>Vehicle registration</td>
                    <td>{Obligation.Registration}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className={styles.progressBar}>
              <div className={styles.bar}>
                {titles.map((title) => {
                  const tileSelected = title.value === Obligation.Status;
                  return (
                    <Fragment key={title.value}>
                      <div
                        className={classnames({
                          [styles.step]: true,
                          [styles.selected]: tileSelected,
                          [styles.done]: tileSelected && Obligation.Status === ObligationStatus.ENFORCEMENT_WARRANT,
                          [styles.donePrev]: title.value === ObligationStatus.NOTICE_OF_FINAL_DEMAND && Obligation.Status === ObligationStatus.ENFORCEMENT_WARRANT,
                        })}>
                        <div className={styles.content}>
                          <span className={styles.text}>{title.display}</span>
                        </div>
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default CourtStep4;
